import React from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap";
import EmptyState from "./EmptyState";
import TableSkeleton from "./TableSkeleton";

/**
 * Reusable shell around any Mafia/DI table. Gives every table the same
 * polished toolbar (search + count + refresh + custom actions), sticky
 * header, loading skeleton, empty state, and error state.
 *
 * This component handles the chrome; the consumer renders the actual
 * <table> body via the `children` render prop.
 *
 * Props:
 *  - title:           card heading text
 *  - subtitle:        small line under the title
 *  - rowCount:        number to display in the count badge
 *  - countLabel:      label after the count (defaults to "rows")
 *  - searchValue:     controlled search value
 *  - onSearchChange:  (string) => void
 *  - searchPlaceholder
 *  - onRefresh:       optional () => void
 *  - refreshing:      bool — disables refresh button + animates icon
 *  - actions:         optional React node — extra buttons to the right
 *  - loading:         bool — show skeleton inside <tbody>
 *  - error:           string | null — show error state inside the body
 *  - empty:           bool — show empty state inside the body
 *  - emptyTitle/emptyMessage/emptyIcon — passed to EmptyState
 *  - skeletonColumns: number — column count for the loading skeleton
 *  - children:        the actual <table>...</table>. Skeleton/empty/error
 *                     blocks are rendered ABOVE the table when active.
 *  - bodyHeight:      px — fixes the scroll area height (sticky header)
 *  - dense:           bool — tighter card padding
 *
 * Style: matches existing dashboard cards (Card / CardHeader / CardBody
 * with bg-white + shadow-sm) and the rest of the Velzon/Skote chrome.
 */
const DataTableShell = ({
  title,
  subtitle,
  rowCount,
  countLabel = "rows",
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  onRefresh,
  refreshing = false,
  actions,
  loading = false,
  error = null,
  empty = false,
  emptyTitle = "No records found",
  emptyMessage = "Try adjusting your search or filters.",
  emptyIcon = "ri-inbox-line",
  skeletonColumns = 5,
  children,
  bodyHeight,
  dense = false,
}) => {
  const headerPad = dense ? "pt-3 px-3 pb-2" : "pt-4 px-4 pb-3";
  const bodyPad = dense ? "p-3" : "p-0";

  return (
    <Card className="shadow-sm border-0">
      <CardHeader className={`bg-white border-0 ${headerPad}`}>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
          <div>
            {title ? (
              <h5 className="mb-1 fw-semibold">{title}</h5>
            ) : null}
            {subtitle ? (
              <p className="text-muted small mb-0">{subtitle}</p>
            ) : null}
          </div>

          {typeof rowCount === "number" ? (
            <Badge
              color="light"
              className="text-muted border fw-medium"
              style={{ padding: "6px 10px", fontSize: 12 }}
            >
              <i className="ri-database-2-line align-bottom me-1" />
              {rowCount.toLocaleString()} {countLabel}
            </Badge>
          ) : null}
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2">
          {typeof onSearchChange === "function" ? (
            <InputGroup style={{ maxWidth: 360, flex: "1 1 240px" }}>
              <InputGroupText className="bg-light border-end-0">
                <i className="ri-search-line" />
              </InputGroupText>
              <Input
                type="text"
                className="border-start-0"
                placeholder={searchPlaceholder}
                value={searchValue || ""}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </InputGroup>
          ) : null}

          <div className="d-flex flex-wrap gap-2 ms-auto">
            {actions}

            {typeof onRefresh === "function" ? (
              <Button
                color="light"
                onClick={onRefresh}
                disabled={refreshing}
                title="Refresh"
              >
                <i
                  className={`ri-refresh-line align-bottom me-1 ${
                    refreshing ? "spin-anim" : ""
                  }`}
                />
                {refreshing ? "Refreshing..." : "Refresh"}
              </Button>
            ) : null}
          </div>
        </div>
      </CardHeader>

      <CardBody className={bodyPad}>
        {error ? (
          <EmptyState
            icon="ri-error-warning-line"
            tone="danger"
            title="Couldn't load data"
            message={error}
            action={
              typeof onRefresh === "function" ? (
                <Button color="primary" onClick={onRefresh}>
                  <i className="ri-refresh-line align-bottom me-1" /> Try again
                </Button>
              ) : null
            }
          />
        ) : (
          <div
            className="position-relative"
            style={{
              // Round M1 — wide tables scroll inside this region instead of
              // stretching the whole page on phones. Vertical behavior is
              // unchanged unless bodyHeight is provided (as before).
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              ...(bodyHeight
                ? { maxHeight: bodyHeight, overflowY: "auto" }
                : null),
            }}
          >
            {loading && !empty ? (
              <table className="table align-middle table-nowrap mb-0">
                <tbody>
                  <TableSkeleton rows={6} columns={skeletonColumns} />
                </tbody>
              </table>
            ) : empty ? (
              <EmptyState
                icon={emptyIcon}
                title={emptyTitle}
                message={emptyMessage}
              />
            ) : (
              children
            )}
          </div>
        )}
      </CardBody>

      <style>{`
        .spin-anim {
          animation: dts-spin 0.9s linear infinite;
          display: inline-block;
        }
        @keyframes dts-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </Card>
  );
};

export default DataTableShell;
